import { Role } from '@/shared/auth/roles';

/**
 * Decodes a JWT token to get its payload.
 * Note: This does not verify the signature, it only decodes the payload.
 */
export const parseJwt = (token: string): any => {
  try {
    const base64Url = token.split('.')[1];
    if (!base64Url) return null;

    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      window
        .atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join(''),
    );

    return JSON.parse(jsonPayload);
  } catch (e) {
    console.error('Failed to parse JWT', e);
    return null;
  }
};

/**
 * Checks if a JWT token is expired.
 * Returns true if expired or invalid, false otherwise.
 */
export const isTokenExpired = (token: string): boolean => {
  try {
    const payload = parseJwt(token);
    if (!payload || !payload.exp) {
      return false;
    }

    const currentTime = Math.floor(Date.now() / 1000);
    return payload.exp < currentTime;
  } catch (_) {
    return true;
  }
};

/**
 * Checks if the user has an admin role based on the token.
 */
export const hasAdminRole = (token: string | null): boolean => {
  if (!token) return false;
  try {
    const payload = parseJwt(token);
    if (!payload) return false;

    // Spring Security OAuth2 JWT includes authorities as array of strings
    const authorities: string[] = payload.authorities ?? payload.roles ?? [];
    return authorities.includes(Role.ADMIN);
  } catch {
    return false;
  }
};

export const hasProfessorRole = (token: string | null): boolean => {
  if (!token) return false;
  const payload = parseJwt(token);
  const authorities: string[] = payload?.authorities ?? payload?.roles ?? [];
  return authorities.includes(Role.PROFESSOR);
};

export const hasStaffRole = (token: string | null): boolean =>
  hasAdminRole(token) || hasProfessorRole(token);
